import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './lib/auth-context'
import Nav from './components/Nav'
import LoginPage from './routes/LoginPage'
import LoadsPage from './routes/LoadsPage'
import CreateLoadPage from './routes/CreateLoadPage'
import LoadDetailPage from './routes/LoadDetailPage'
import CarrierBoardPage from './routes/CarrierBoardPage'
import DriverJobsPage from './routes/DriverJobsPage'
import DriverJobDetailPage from './routes/DriverJobDetailPage'
import InvoicesPage from './routes/InvoicesPage'
import InvoiceDetailPage from './routes/InvoiceDetailPage'
import DriverAppPage from './routes/DriverAppPage'
import DispatcherPage from './routes/DispatcherPage'
import AccountingPage from './routes/AccountingPage'
import DemoStatusPage from './routes/DemoStatusPage'
import TrailerPlannerPage from './routes/TrailerPlannerPage'
import PodViewerPage from './routes/PodViewerPage'
import RateConfirmationPage from './routes/RateConfirmationPage'
import DispatchSheetPage from './routes/DispatchSheetPage'
import BolPage from './routes/BolPage'
import SettingsPage from './routes/SettingsPage'
import DriversSettingsPage from './routes/DriversSettingsPage'
import DriverProfileSettingsPage from './routes/DriverProfileSettingsPage'
import CarrierEquipmentPage from './routes/CarrierEquipmentPage'
import CarrierFleetFinancePage from './routes/CarrierFleetFinancePage'
import CarrierDriversPage from './routes/CarrierDriversPage'
import CarrierDriverDetailPage from './routes/CarrierDriverDetailPage'
import DriverSettingsPage from './routes/DriverSettingsPage'
import DriverTrailerLoaderPage from './routes/DriverTrailerLoaderPage'
import DriverLoadPlanMobilePage from './routes/DriverLoadPlanMobilePage'
import CarrierDashboardPage from './routes/CarrierDashboardPage'
import ImportsPage from './routes/ImportsPage'
import QuotesPage from './routes/QuotesPage'
import QuoteNewPage from './routes/QuoteNewPage'
import QuoteDetailPage from './routes/QuoteDetailPage'
import QuotePortPage from './routes/QuotePortPage'
import QuoteResponsePage from './routes/QuoteResponsePage'
import CustomerPortalPage from './routes/CustomerPortalPage'
import OrdersPage from './routes/OrdersPage'
import CreateOrderPage from './routes/CreateOrderPage'
import OrderDetailPage from './routes/OrderDetailPage'
import SearchPage from './routes/SearchPage'
import CustomersPage from './routes/CustomersPage'
import CustomerNewPage from './routes/CustomerNewPage'
import CustomerDetailPage from './routes/CustomerDetailPage'
import PlacesPage from './routes/PlacesPage'
import PlaceNewPage from './routes/PlaceNewPage'
import PlaceDetailPage from './routes/PlaceDetailPage'
import LanesPage from './routes/LanesPage'
import LaneNewPage from './routes/LaneNewPage'
import LaneDetailPage from './routes/LaneDetailPage'
import VinInventoryPage from './routes/VinInventoryPage'
import VinInventoryImportPage from './routes/VinInventoryImportPage'
import VinBatchesPage from './routes/VinBatchesPage'
import VinBatchDetailPage from './routes/VinBatchDetailPage'
import DispatchBoardPage from './routes/DispatchBoardPage'
import LoadTrailerPlannerPage from './routes/LoadTrailerPlannerPage'
import CarriersPage from './routes/CarriersPage'
import CarrierNewPage from './routes/CarrierNewPage'
import CarrierDetailPage from './routes/CarrierDetailPage'
import PublicInspectionPage from './routes/PublicInspectionPage'
import AdminHomePage from './routes/AdminHomePage'
import PlatformOwnerHomePage from './routes/PlatformOwnerHomePage'
import { getDefaultRouteForUser } from './lib/role-routing'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading…</div>
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RequireRoles({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const { user, loading, hasRole } = useAuth()
  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading…</div>
  if (!user) return <Navigate to="/login" replace />
  if (!hasRole(...roles)) return <Navigate to={getDefaultRouteForUser(user)} replace />
  return <>{children}</>
}

function RoleHomeRedirect() {
  const { user, loading } = useAuth()
  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading…</div>
  return <Navigate to={getDefaultRouteForUser(user)} replace />
}

function App() {
  const dispatcherRoles = ['DISPATCHER', 'BROKER_ADMIN', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER', 'DEALER', 'SHIPPER']
  const driverRoles = ['DRIVER', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER']
  const carrierRoles = ['CARRIER_ADMIN', 'CARRIER_OPERATIONS', 'DISPATCHER', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER']
  const accountingRoles = ['ACCOUNTING', 'BROKER_ADMIN', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER']
  const adminRoles = ['ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER', 'PLATFORM_OWNER']
  const dispatchBoardRoles = ['BROKER_ADMIN', 'DEALER', 'SHIPPER', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER']
  const trailerPlannerRoles = ['DISPATCHER', 'BROKER_ADMIN', 'DEALER', 'SHIPPER', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'OWNER']
  const platformOwnerRoles = ['PLATFORM_OWNER', 'AUX_ADMIN', 'SUPER_ADMIN']

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="lg:flex">
        <Nav />
        <main className="flex-1">
          <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route path="/loads" element={<RequireAuth><LoadsPage /></RequireAuth>} />
        <Route path="/loads/new" element={<RequireRoles roles={dispatcherRoles}><CreateLoadPage /></RequireRoles>} />
        <Route path="/loads/:loadId" element={<RequireAuth><LoadDetailPage /></RequireAuth>} />
        <Route path="/loads/:loadId/trailer" element={<RequireRoles roles={trailerPlannerRoles}><LoadTrailerPlannerPage /></RequireRoles>} />
        <Route path="/quote-desk" element={<RequireRoles roles={dispatcherRoles}><QuotesPage /></RequireRoles>} />
        <Route path="/quotes" element={<RequireRoles roles={dispatcherRoles}><Navigate to="/quote-desk" replace /></RequireRoles>} />
        <Route path="/quotes/new" element={<RequireRoles roles={dispatcherRoles}><QuoteNewPage /></RequireRoles>} />
        <Route path="/quotes/:quoteId" element={<RequireRoles roles={dispatcherRoles}><QuoteDetailPage /></RequireRoles>} />
        <Route path="/quote-requests" element={<RequireRoles roles={dispatcherRoles}><Navigate to="/quote-desk" replace /></RequireRoles>} />
        <Route path="/q/:accountSlug" element={<QuotePortPage />} />
        <Route path="/quote-response/:token" element={<QuoteResponsePage />} />
        <Route path="/customer-portal/:token" element={<CustomerPortalPage />} />
        <Route path="/admin" element={<RequireRoles roles={adminRoles}><AdminHomePage /></RequireRoles>} />
        <Route path="/platform-owner" element={<RequireRoles roles={platformOwnerRoles}><PlatformOwnerHomePage /></RequireRoles>} />
        <Route path="/inspect/:publicToken" element={<PublicInspectionPage />} />
        <Route path="/orders" element={<RequireRoles roles={dispatcherRoles}><OrdersPage /></RequireRoles>} />
        <Route path="/orders/new" element={<RequireRoles roles={dispatcherRoles}><CreateOrderPage /></RequireRoles>} />
        <Route path="/orders/:orderId" element={<RequireRoles roles={dispatcherRoles}><OrderDetailPage /></RequireRoles>} />
        <Route path="/customers" element={<RequireRoles roles={dispatcherRoles}><CustomersPage /></RequireRoles>} />
        <Route path="/customers/new" element={<RequireRoles roles={dispatcherRoles}><CustomerNewPage /></RequireRoles>} />
        <Route path="/customers/:id" element={<RequireRoles roles={dispatcherRoles}><CustomerDetailPage /></RequireRoles>} />
        <Route path="/places" element={<RequireRoles roles={dispatcherRoles}><PlacesPage /></RequireRoles>} />
        <Route path="/places/new" element={<RequireRoles roles={dispatcherRoles}><PlaceNewPage /></RequireRoles>} />
        <Route path="/places/:id" element={<RequireRoles roles={dispatcherRoles}><PlaceDetailPage /></RequireRoles>} />
        <Route path="/lanes" element={<RequireRoles roles={dispatcherRoles}><LanesPage /></RequireRoles>} />
        <Route path="/lanes/new" element={<RequireRoles roles={dispatcherRoles}><LaneNewPage /></RequireRoles>} />
        <Route path="/lanes/:laneId" element={<RequireRoles roles={dispatcherRoles}><LaneDetailPage /></RequireRoles>} />
        <Route path="/vin-inventory" element={<RequireRoles roles={dispatcherRoles}><VinInventoryPage /></RequireRoles>} />
        <Route path="/vin-inventory/import" element={<RequireRoles roles={dispatcherRoles}><VinInventoryImportPage /></RequireRoles>} />
        <Route path="/vin-batches" element={<RequireRoles roles={dispatcherRoles}><VinBatchesPage /></RequireRoles>} />
        <Route path="/vin-batches/:batchId" element={<RequireRoles roles={dispatcherRoles}><VinBatchDetailPage /></RequireRoles>} />
        <Route path="/dispatch-board" element={<RequireRoles roles={dispatchBoardRoles}><DispatchBoardPage /></RequireRoles>} />
        <Route path="/carriers" element={<RequireRoles roles={dispatcherRoles}><CarriersPage /></RequireRoles>} />
        <Route path="/carriers/new" element={<RequireRoles roles={dispatcherRoles}><CarrierNewPage /></RequireRoles>} />
        <Route path="/carriers/:id" element={<RequireRoles roles={dispatcherRoles}><CarrierDetailPage /></RequireRoles>} />
        <Route path="/search" element={<RequireAuth><SearchPage /></RequireAuth>} />
        <Route path="/loads/:loadId/rate-confirmation" element={<RequireRoles roles={[...dispatcherRoles, ...accountingRoles]}><RateConfirmationPage /></RequireRoles>} />
        <Route path="/dispatcher/load-board" element={<RequireAuth><LoadsPage /></RequireAuth>} />

        <Route path="/dispatcher" element={<RequireRoles roles={dispatcherRoles}><DispatcherPage /></RequireRoles>} />
        <Route path="/driver/app" element={<RequireRoles roles={driverRoles}><DriverAppPage /></RequireRoles>} />
        <Route path="/accounting" element={<RequireRoles roles={accountingRoles}><AccountingPage /></RequireRoles>} />

        <Route path="/carrier/dashboard" element={<RequireRoles roles={carrierRoles}><CarrierDashboardPage /></RequireRoles>} />
        <Route path="/carrier/loads" element={<RequireRoles roles={carrierRoles}><CarrierBoardPage /></RequireRoles>} />
        <Route path="/carrier/jobs" element={<RequireRoles roles={carrierRoles}><CarrierBoardPage /></RequireRoles>} />

        <Route path="/driver/jobs" element={<RequireRoles roles={driverRoles}><DriverJobsPage /></RequireRoles>} />
        <Route path="/driver/jobs/:jobId" element={<RequireRoles roles={driverRoles}><DriverJobDetailPage /></RequireRoles>} />
        <Route path="/driver/jobs/:jobId/trailer" element={<RequireRoles roles={driverRoles}><TrailerPlannerPage /></RequireRoles>} />
        <Route path="/driver/loads/:loadId/load-plan" element={<RequireRoles roles={driverRoles}><DriverLoadPlanMobilePage /></RequireRoles>} />
        <Route path="/driver/trailer-loader" element={<RequireRoles roles={driverRoles}><DriverTrailerLoaderPage /></RequireRoles>} />

        <Route path="/invoices" element={<RequireRoles roles={accountingRoles}><InvoicesPage /></RequireRoles>} />
        <Route path="/invoices/:invoiceId" element={<RequireRoles roles={accountingRoles}><InvoiceDetailPage /></RequireRoles>} />
        <Route path="/demo-status" element={<RequireAuth><DemoStatusPage /></RequireAuth>} />
        <Route path="/imports" element={<RequireRoles roles={adminRoles}><ImportsPage /></RequireRoles>} />

        {/* Dispatcher trailer planner — full drag-and-drop editor */}
        <Route path="/trailer/:jobId" element={<RequireRoles roles={dispatcherRoles}><TrailerPlannerPage /></RequireRoles>} />
        <Route path="/jobs/:jobId/trailer" element={<RequireRoles roles={dispatcherRoles}><TrailerPlannerPage /></RequireRoles>} />
        <Route path="/jobs/:jobId/dispatch-sheet" element={<RequireAuth><DispatchSheetPage /></RequireAuth>} />
        <Route path="/jobs/:jobId/bol" element={<RequireAuth><BolPage /></RequireAuth>} />
        <Route path="/jobs/:jobId" element={<RequireAuth><DriverJobDetailPage /></RequireAuth>} />
        <Route path="/jobs/:jobId/pod" element={<RequireAuth><PodViewerPage /></RequireAuth>} />

        {/* Settings */}
        <Route path="/settings" element={<RequireRoles roles={adminRoles}><SettingsPage /></RequireRoles>} />
        <Route path="/settings/drivers" element={<RequireRoles roles={carrierRoles}><DriversSettingsPage /></RequireRoles>} />
        <Route path="/settings/drivers/:driverId" element={<RequireRoles roles={carrierRoles}><DriverProfileSettingsPage /></RequireRoles>} />

        {/* Carrier admin: fleet + driver management */}
        <Route path="/carrier/equipment" element={<RequireRoles roles={carrierRoles}><CarrierEquipmentPage /></RequireRoles>} />
        <Route path="/carrier/fleet/finance" element={<RequireRoles roles={adminRoles}><CarrierFleetFinancePage /></RequireRoles>} />
        <Route path="/carrier/drivers" element={<RequireRoles roles={carrierRoles}><CarrierDriversPage /></RequireRoles>} />
        <Route path="/carrier/drivers/:driverId" element={<RequireRoles roles={carrierRoles}><CarrierDriverDetailPage /></RequireRoles>} />

        {/* Driver self-service */}
        <Route path="/driver/settings" element={<RequireRoles roles={driverRoles}><DriverSettingsPage /></RequireRoles>} />

        <Route path="/" element={<RoleHomeRedirect />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

export default App
